import { useRouter } from 'next/router'
import { useRef } from 'react'
import { LuListFilter, LuX } from 'react-icons/lu'

import { FeatureFlag, WhenFlag } from '@/feature/feature-flags'
import { TagsList } from '@/feature/tags/components/TagsList/TagsList'
import { useGlobalOrgTags } from '@/feature/tags/hooks/useGlobalOrgTags'
import { useTagsStore } from '@/feature/tags/store'

import { Badge, BadgeVariant } from '@/lib/core/components/Badge'

import { HEADER_MAX_VISIBLE_TAGS, supportedUrls } from './TableHeaderTags.constants'
import styles from './TableHeaderTags.module.css'
import { ModalOpenVariant, TableTagsHeaderProps } from './TableHeaderTags.types'

export const TableHeaderTags = ({
  displayTags = true,
  shouldDisplay = false,
}: TableTagsHeaderProps) => {
  const router = useRouter()
  const { asPath } = router
  const { toggleFilterPopout, filteringTags, openModal, setFilteringTags } = useTagsStore()

  const filterButtonRef = useRef<HTMLDivElement>(null)

  // Ensure the component gets updated when tags are modified
  useGlobalOrgTags()

  const isSupported = supportedUrls.some(
    (prefix) => asPath.startsWith(prefix) || asPath.split('?')[0] === prefix,
  )

  if (!isSupported) {
    return null
  }

  const handleFilterButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent event bubbling
    const rect = e.currentTarget.getBoundingClientRect()
    toggleFilterPopout({
      position: { x: rect.left, y: rect.bottom },
    })
  }

  if (!shouldDisplay) {
    return null
  }

  return (
    <FeatureFlag flag="assetTags">
      <WhenFlag>
        <span className={styles.wrapper} data-testid="table-tags-header">
          <div>
            <div
              ref={filterButtonRef}
              className={styles.button}
              data-testid="table-tags-button"
              onClick={handleFilterButtonClick}
            >
              <LuListFilter size="16" />
              Tags
              {filteringTags.length > 0 && (
                <>
                  <Badge
                    minSize={20}
                    variant={BadgeVariant.TAG}
                    backgroundColor="#314663"
                    textColor="#fff"
                  >
                    {filteringTags.length}
                  </Badge>
                  <LuX
                    onClick={(e) => {
                      e.stopPropagation()
                      setFilteringTags(null)
                    }}
                    size="16"
                  />
                </>
              )}
            </div>
          </div>
          <div className={styles.tagsContainer}>
            {filteringTags.length > 0 && displayTags && (
              <TagsList
                tagsList={filteringTags}
                maxTagsRenderCount={HEADER_MAX_VISIBLE_TAGS}
                openModal={openModal}
                modalVariant={ModalOpenVariant.FILTER}
                filterButtonRef={filterButtonRef}
              />
            )}
          </div>
        </span>
      </WhenFlag>
    </FeatureFlag>
  )
}
